import React, { useContext, useState, useEffect } from "react";
import CustomTable from "../../components/CustomTable";
import KyuAdd from "./KyuAdd";
import useApi from "../../hooks/useApi";
import { ListKyu, ListBelt } from "../../constants/API/API";
import useFilter from "../../hooks/useFilter";
import KyuUpdate from "./KyuUpdate";
import { AppStateContext } from "../../context/AppContext";
import { StatusChangeKyu, deleteKyu } from "../../constants/API/API";
import { message, Select, Space, Button, Switch } from "antd";
import moment from "moment";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import KyuView from "./KyuView";
import DeleteRecord from "../../components/DeleteRecord";

const Kyu = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Kyu Management");
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(new Date().getTime());
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedKyu, setSelectedKyu] = useState(null);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [beltList, setBeltList] = useState([]);
  const [viewVisible, setViewVisible] = useState(false);
  const [filters, setFilters] = useFilter({ sortField: "createdAt", sort: "desc", startDate: null, endDate: null, beltId: undefined, });

  useEffect(() => {
    async function fetchBeltList() {
      try {
        const [beltRes] = await Promise.all([ListBelt()]);
        setBeltList(beltRes?.data?.list || []);
      } catch (e) {
        message.error("Failed to fetch belt list");
        setBeltList([]);
      }
    }
    fetchBeltList();
  }, []);

  const addKyu = () => {
    setAddModal(true);
  };

  const handleStatusChange = async (checked, kyu) => {
    setLoading(true);
    try {
      await StatusChangeKyu(kyu._id, { isActive: checked });
      message.success(`Kyu ${checked ? "activated" : "deactivated"} successfully`);
      setRefresh(new Date().getTime());
    } catch (error) {
      message.error("Failed to update status");
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedKyu(record);
    setEditModal(true);
  };

  const handleBeltFilter = (value) => {
    setFilters((prev) => ({
      ...prev,
      beltId: value,
      page: 1,
    }));
  };

  const handleView = (record) => {
    setSelectedKyu(record);
    setViewVisible(true);
  };

  useApi({
    callback: ListKyu,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: (data) => {
      setData(data?.list?.docs || []);
      setTotalDocs(data?.list?.totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Belt",
      dataIndex: "beltId",
      key: "beltId",
      render: (_, item) => item?.belt?.name || "-",
    },
    {
      title: "Branch Name",
      dataIndex: "branchId",
      key: "branchId",
      render: (_, item) => item?.belt?.branch?.name || "-",
    },
    {
      title: "Classes Required",
      dataIndex: "requiredClasses",
      key: "requiredClasses",
    },
    {
      title: "Priority Number",
      dataIndex: "priorityNumber",
      key: "priorityNumber",
      render: (value) => (value !== null && value !== undefined && value !== "" ? value : "N/A"),
    },

    // {
    //   title: "Always Eligible",
    //   dataIndex: "alwaysEligible",
    //   key: "alwaysEligible",
    //   render: (val) => (val ? "Yes" : "No"),
    // },
    {
      title: "Triggers Graduation",
      dataIndex: "triggersGraduationEligibility",
      key: "triggersGraduationEligibility",
      render: (val) => (val ? "Yes" : "No"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(checked, record)}
        />
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("ll"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="middle">
          <Button type="primary" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          <Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <DeleteRecord
            _id={record._id}
            callback={() => deleteKyu(record._id, { isDeleted: true })}
            setLoading={setLoading}
            setRefresh={setRefresh}
            trigger={true}
            type="Kyu"
          />
        </Space>
      ),
    },
  ];

  const renderFilters = () => {
    return (
      <Space>
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Belt"
          onChange={handleBeltFilter}
          value={filters.beltId}
        >
          <Select.Option value="">All Belts</Select.Option>
          {beltList.map((belt) => (
            <Select.Option key={belt._id} value={belt._id}>
              {belt.name}
            </Select.Option>
          ))}
        </Select>
      </Space>
    );
  };

  return (
    <div>
      <CustomTable
        addFn={addKyu}
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["name"]}
        searchPlaceholder="Search by Kyu Name"
        customFilter={renderFilters}
      />

      {addModal && (
        <KyuAdd visible={addModal} setVisible={setAddModal} refresh={setRefresh} />
      )}
      {editModal && selectedKyu && (
        <KyuUpdate
          visible={editModal}
          setVisible={setEditModal}
          selected={selectedKyu?._id}
          refresh={setRefresh}
        />
      )}
      {viewVisible && selectedKyu && (
        <KyuView
          viewVisible={viewVisible}
          setViewVisible={(visible) => {
            setViewVisible(visible);
            if (!visible) setSelectedKyu(null);
          }}
          selectedKyu={selectedKyu}
        />
      )}
    </div>
  );
};

export default Kyu;
